import { CreateElement } from "./element";

type LayoutPreset =
    | "A4"
    | "A3"
    | "letter"
    | "legal"
    | "A4-landscape"
    | "A3-landscape"
    | "letter-landscape"
    | "legal-landscape"
    | "custom";

interface OptionsSection {
    /** Bidi */
    bidi?: boolean;
    /** Internal code number */
    code?: number;
    /** Exclude headers and footers reference tags. Default as false */
    excludeHeadersFooters?: boolean;
    /** Height: twips value */
    height?: number;
    /** Margins: twips value */
    margin?: {
        top?: number;
        right?: number;
        bottom?: number;
        left?: number;
        header?: number;
        footer?: number;
    };
    /** Number of columns */
    numberColumns?: number;
    /** Orientation */
    orient?: "portrait" | "landscape";
    /** RTL gutter */
    rtlGutter?: boolean;
    /** Sizes. If undefined use the previous section from the document */
    size?: LayoutPreset;
    /** Type. Default as nextPage */
    type?: "nextPage" | "nextColumn" | "continuous" | "evenPage" | "oddPage";
    /** Width: twips value */
    width?: number;
}

interface OptionsSectionLayout extends OptionsSection {
    /** If true only modifies the last section. Default as false */
    onlyLastSection?: boolean;
}

/**
 * Create section
 */
class CreateSection extends CreateElement {
    /**
     * Element options
     */
    protected options: OptionsSection;

    /**
     * Section init contents
     */
    protected sectionInit: string;

    /**
     * Constructor
     *
     * @param options - Options section
     */
    constructor(options: OptionsSection) {
        super();

        // default options
        options.excludeHeadersFooters ??= false;
        options.type ??= "nextPage";

        this.options = options;

        // merge preset sizes and options if a preset size is set
        if (this.options.size != null) {
            this.options = {
                ...this.getSizePreset(this.options.size),
                ...this.options,
            };
        }

        this.sectionInit = "";
    }

    /**
     * Setter sectionInit
     *
     * @param content - Section init contents
     */
    public setSectionInit(content: string) {
        this.sectionInit = content;
    }

    /**
     * Creates content
     *
     * @returns Contents
     */
    public create(): string {
        const contents = "<w:p><w:pPr>" + this.sectionInit + "</w:pPr></w:p>";

        return contents;
    }

    /**
     * Getter options
     *
     * @returns Section options
     */
    public getOptions(): OptionsSection {
        return this.options;
    }

    /**
     * Gets size preset
     *
     * @param preset - Preset
     * @returns Size preset
     */
    protected getSizePreset(preset: LayoutPreset): OptionsSection | null {
        const marginLandscape = {
            top: 1701,
            right: 1417,
            bottom: 1701,
            left: 1417,
            header: 708,
            footer: 708,
        };
        const marginPortrait = {
            top: 1417,
            right: 1701,
            bottom: 1417,
            left: 1701,
            header: 708,
            footer: 708,
        };
        const A4 = {
            width: 11906,
            height: 16838,
            numberColumns: 1,
            orient: "portrait" as const,
            code: 9,
            margin: marginPortrait,
            gutter: 0,
        };
        const A4landscape = {
            width: 16838,
            height: 11906,
            numberColumns: 1,
            orient: "landscape" as const,
            code: 9,
            margin: marginLandscape,
            gutter: 0,
        };
        const A3 = {
            width: 16839,
            height: 23814,
            numberColumns: 1,
            orient: "portrait" as const,
            code: 8,
            margin: marginPortrait,
            gutter: 0,
        };
        const A3landscape = {
            width: 23814,
            height: 16839,
            numberCols: 1,
            orient: "landscape" as const,
            code: 8,
            margin: marginLandscape,
            gutter: 0,
        };
        const letter = {
            width: 12240,
            height: 15840,
            numberCols: 1,
            orient: "portrait" as const,
            code: 1,
            margin: marginPortrait,
            gutter: 0,
        };
        const letterLandscape = {
            width: 15840,
            height: 12240,
            numberCols: 1,
            orient: "landscape" as const,
            code: 1,
            margin: marginLandscape,
            gutter: 0,
        };
        const legal = {
            width: 12240,
            height: 20160,
            numberCols: 1,
            orient: "portrait" as const,
            code: 5,
            margin: marginPortrait,
            gutter: 0,
        };
        const legalLandscape = {
            width: 20160,
            height: 12240,
            numberCols: 1,
            orient: "landscape" as const,
            code: 1,
            margin: marginLandscape,
            gutter: 0,
        };

        switch (preset) {
            case "A4":
                return A4;
            case "A4-landscape":
                return A4landscape;
            case "A3":
                return A3;
            case "A3-landscape":
                return A3landscape;
            case "letter":
                return letter;
            case "letter-landscape":
                return letterLandscape;
            case "legal":
                return legal;
            case "legal-landscape":
                return legalLandscape;
        }

        return null;
    }
}

export { CreateSection, LayoutPreset, OptionsSection, OptionsSectionLayout };
